import { useMemo } from "react";

import Feedback from "ds/components/Feedback";
import useTypedContext from "hooks/useTypedContext";
import useTypedFlags from "hooks/useTypedFlags";
import { StackSettingsContextData } from "views/Stack/Settings/Context";
import FeedbackActions from "ds/components/Feedback/Actions";
import Button from "ds/components/Button";
import { ArrowCircle } from "components/icons/generated";
import { VENDOR_KEY } from "constants/vendor";

import { showStackSettingsTerragruntMigrationConfirmationModal } from "./ConfirmationModal";
import { showStackSettingsTerragruntRollbackConfirmationModal } from "./RollbackConfirmationModal";

type StackSettingsTerragruntMigrationProps = {
  vendorKey?: VENDOR_KEY;
};

const StackSettingsTerragruntMigration = ({ vendorKey }: StackSettingsTerragruntMigrationProps) => {
  const { stackSettings } = useTypedContext(StackSettingsContextData);
  const { terragruntStackMigration } = useTypedFlags();

  const canBeMigrated = useMemo(
    () =>
      vendorKey === VENDOR_KEY.TERRAFORM &&
      stackSettings.labels?.some((label) => label === "terragrunt"),
    [stackSettings.labels, vendorKey]
  );

  const canRollbackMigration = useMemo(
    () => vendorKey === VENDOR_KEY.TERRAGRUNT && stackSettings.wasMigratedFromTerraform,
    [stackSettings, vendorKey]
  );

  const handleMigrateToTerragrunt = () => {
    showStackSettingsTerragruntMigrationConfirmationModal({
      stackId: stackSettings.id,
    });
  };

  const handleRollbackToTerraform = () => {
    showStackSettingsTerragruntRollbackConfirmationModal({
      stackId: stackSettings.id,
    });
  };

  if (!terragruntStackMigration) {
    return null;
  }

  if (canBeMigrated) {
    return (
      <Feedback type="callout" variant="warning" title="Terragrunt via labels will be deprecated">
        This stack uses Terragrunt through labels on a Terraform stack. We recommend migrating to a
        native Terragrunt stack for better support and future compatibility.
        <FeedbackActions>
          <Button variant="contrast" size="small" onPress={handleMigrateToTerragrunt}>
            Migrate to Terragrunt
          </Button>
        </FeedbackActions>
      </Feedback>
    );
  }

  if (canRollbackMigration) {
    return (
      <Feedback type="callout" variant="info">
        If you're having issues with Terragrunt migration, you can roll it back to Terraform.
        <FeedbackActions>
          <Button
            variant="secondary"
            size="small"
            startIcon={ArrowCircle}
            onPress={handleRollbackToTerraform}
          >
            Rollback to Terraform
          </Button>
        </FeedbackActions>
      </Feedback>
    );
  }

  return null;
};

export default StackSettingsTerragruntMigration;
